// src/components/features/departures/StopDetailsPage/StopDetailsPage.tsx
import type {
  DisruptionLink,
  Stop,
  StopDetail,
} from '../../../../arcticapi/types';
import type { DepartureModel } from '../DepartureItem/DepartureItem';
import { DisruptionAlert } from '../../../base/DisruptionAlert/DisruptionAlert';
import { Heading } from '../../../base/Heading/Heading';
import { StateMessage } from '../../../base/StateMessage/StateMessage';
import { DetailsPage, PagePadding } from '../../../widget/Pages/Pages';
import { DepartureList } from '../DepartureList/DepartureList';
import { StopInfo } from '../StopInfo/StopInfo';
import { StateWrapper } from '../../../base/StateWrapper/StateWrapper';
import { EmptyState } from '../../../base/EmptyState/EmptyState';
import { Flex } from '../../../base/Flex/Flex';

interface StopDetailsPageProps {
  departuresStop: StopDetail | Stop | null;
  departuresStopName: string | null;
  departuresDisruption: DisruptionLink | null;
  departuresStreetViewLink: string | null;
  departuresLoading: boolean;
  departuresError: Error | null;
  departures: DepartureModel[];
  autoRefreshEnabled: boolean;
  onAutoRefreshChange: (enabled: boolean) => void;
  onRefresh?: () => void;
  countdownSecondsRemaining: number | null;
  departuresLastUpdatedTime: string | null;
}

/**
 * Departures details page with selected stop info and departure list states.
 */
export function StopDetailsPage({
  departuresStop,
  departuresStopName,
  departuresDisruption,
  departuresStreetViewLink,
  departuresLoading,
  departuresError,
  departures,
  autoRefreshEnabled,
  onAutoRefreshChange,
  onRefresh,
  countdownSecondsRemaining,
  departuresLastUpdatedTime,
}: StopDetailsPageProps) {
  return (
    <DetailsPage data-details-page="stop">
      <PagePadding>
        {departuresStop ? (
          <StopInfo
            stop={departuresStop}
            streetViewLink={departuresStreetViewLink}
          />
        ) : (
          <Heading as="h3" variant="md">
            {departuresStopName
              ? `Departures from ${departuresStopName}`
              : 'Stop departures'}
          </Heading>
        )}
      </PagePadding>

      {departuresDisruption && (
        <Flex mt="base">
          <DisruptionAlert disruption={departuresDisruption} />
        </Flex>
      )}

      <StateWrapper
        loading={departuresLoading}
        error={departuresError}
        isEmpty={departures.length === 0}
        renderError={(error) => (
          <StateMessage
            state="error"
            title="Could not load departures"
            message={error.message}
          />
        )}
        renderEmpty={() => (
          <EmptyState
            title="No departures found"
            message="Try a different stop or check again shortly."
          />
        )}
      >
        <DepartureList
          departures={departures}
          autoRefreshEnabled={autoRefreshEnabled}
          onAutoRefreshChange={onAutoRefreshChange}
          countdownSecondsRemaining={countdownSecondsRemaining}
          lastUpdatedTime={departuresLastUpdatedTime}
          paused={!autoRefreshEnabled}
          onRefresh={onRefresh}
        />
      </StateWrapper>
    </DetailsPage>
  );
}
